import { useState, useEffect } from "react";
import axios from "axios";
import { Navbar } from "../../components/layout/Navbar";
import { Footer } from "../../components/layout/Footer";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Package, Phone, Calendar, FileText } from "lucide-react";
import Invoice from "../../components/Invoice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_NUMBER = "218915224966";

const CustomerOrders = () => {
  const { user, getAuthHeaders } = useAuth();
  const { t, isRTL } = useLanguage();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [invoiceOrder, setInvoiceOrder] = useState(null);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await axios.get(`${API}/orders`, {
          headers: getAuthHeaders(),
        });
        setOrders(response.data);
      } catch (error) {
        console.error("Failed to fetch orders:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [getAuthHeaders]);

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      processing: "bg-blue-100 text-blue-800",
      out_for_delivery: "bg-purple-100 text-purple-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: t("pending"),
      processing: t("processing"),
      out_for_delivery: t("outForDelivery"),
      delivered: t("delivered"),
      cancelled: t("cancelled"),
    };
    return labels[status] || status;
  };

  const filterLabels = {
    all: t("all"),
    pending: t("pending"),
    processing: t("processing"),
    out_for_delivery: t("outForDelivery"),
    delivered: t("delivered"),
    cancelled: t("cancelled"),
  };

  const filteredOrders = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const getWhatsAppLink = (order) => {
    const message = encodeURIComponent(
      isRTL ? `مرحباً، لدي سؤال حول الطلب رقم #${order.id.slice(0, 8)}` : `Hi, I have a question about my order #${order.id.slice(0, 8)}`
    );
    return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Navbar />
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8" data-testid="customer-orders">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold mb-2">{t("myOrders")}</h1>
          <p className="text-muted-foreground">{t("trackOrders")}</p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {["all", "pending", "processing", "out_for_delivery", "delivered", "cancelled"].map((status) => (
            <Button
              key={status}
              variant={filter === status ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(status)}
              className={filter === status ? "bg-[#1a365d]" : ""}
            >
              {filterLabels[status]}
            </Button>
          ))}
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-40 bg-muted animate-pulse rounded-xl" />
            ))}
          </div>
        ) : filteredOrders.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-12 text-center">
              <Package className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="font-heading font-semibold text-lg mb-2">{t("noOrdersFound")}</h3>
              <p className="text-muted-foreground">
                {filter === "all" ? t("youHaventPlacedOrders") : `${t("noFilteredOrders")} ${filterLabels[filter]}`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredOrders.map((order) => (
              <Card key={order.id} className="border-0 shadow-sm overflow-hidden">
                <CardHeader className="bg-slate-50 py-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <CardTitle className="font-heading text-lg">
                        {t("order")} #{order.id.slice(0, 8)}
                      </CardTitle>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {new Date(order.created_at).toLocaleDateString(isRTL ? 'ar-LY' : 'en-US')}
                        </span>
                        <span>{order.items?.length || 0} {t("items")}</span>
                      </div>
                    </div>
                    <Badge className={getStatusColor(order.status)}>
                      {getStatusLabel(order.status)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  {/* Order Items */}
                  <div className="space-y-3 mb-6">
                    {order.items?.map((item, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <div>
                          <p className="font-medium">{item.product_name}</p>
                          <p className="text-sm text-muted-foreground">{t("qty")}: {item.quantity}</p>
                        </div>
                        <p className="font-semibold">{item.total} {t("lyd")}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Order Total */}
                  <div className="flex justify-between items-center pt-4 border-t">
                    <span className="font-medium">{t("total")}</span>
                    <span className="text-xl font-bold text-[#1a365d]">{order.total} {t("lyd")}</span>
                  </div>
                  
                  {/* Driver Info */}
                  {order.driver_name && order.status === "out_for_delivery" && (
                    <div className="mt-4 p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm font-medium text-purple-800">{t("driver")}: {order.driver_name}</p>
                      {order.driver_phone && (
                        <a
                          href={`https://wa.me/${order.driver_phone.replace(/\D/g, "")}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 text-sm text-purple-600 hover:underline mt-1"
                        >
                          <Phone className="h-4 w-4" />
                          {t("contactDriver")}
                        </a>
                      )}
                    </div>
                  )}
                  
                  {/* Actions */}
                  <div className="mt-6 flex gap-3">
                    <Button 
                      variant="outline" 
                      className="flex-1 border-[#1a365d] text-[#1a365d] hover:bg-[#1a365d]/10"
                      onClick={() => setInvoiceOrder(order)}
                      data-testid={`invoice-btn-${order.id}`}
                    >
                      <FileText className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} />
                      {t("printInvoice")}
                    </Button>
                    <a href={getWhatsAppLink(order)} target="_blank" rel="noopener noreferrer" className="flex-1">
                      <Button variant="outline" className="w-full border-green-500 text-green-600 hover:bg-green-50">
                        <svg className={`w-4 h-4 ${isRTL ? 'ml-2' : 'mr-2'}`} fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        {t("support")}
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Invoice Modal */}
      <Invoice 
        isOpen={!!invoiceOrder} 
        onClose={() => setInvoiceOrder(null)} 
        order={invoiceOrder}
        customer={user}
      />

      <Footer />
    </div>
  );
};

export default CustomerOrders;
